// src/api.js – NextScenes API helper (DEV + PROD explicit base)
// Build: 2026-05-25-public-homepage-weekly-mystery-v1
//
// CSRF strategy (Phase 1 hardened, HttpOnly cookie compatible):
// - Backend sets cookie "ns.csrf" as HttpOnly AND returns token from GET /api/csrf
// - Frontend NEVER reads document.cookie for CSRF (HttpOnly makes that impossible)
// - Frontend sends header "X-CSRF-Token" for POST/PUT/PATCH/DELETE
// - Send CSRF header for auth-changing routes too; backend also checks Origin/Referer as a safe fallback
// - Do NOT send CSRF header for /api/contact (backend exempts; public form)
// - CSRF priming failures never crash the actual request
// - Still retries once on true CSRF 403 by refetching /api/csrf

function cleanOrigin(s) {
  return String(s || "").trim().replace(/\/+$/, "");
}

function isLocalhostHost() {
  try {
    const h = String(window.location.hostname || "").toLowerCase();
    return h === "localhost" || h === "127.0.0.1";
  } catch {
    return false;
  }
}

function computeApiBase() {
  const envBase = cleanOrigin(
    process.env.REACT_APP_API_BASE || ""
  );

  if (envBase) return envBase;

  if (typeof window !== "undefined" && isLocalhostHost()) {
    return "http://localhost:4000";
  }

  return "https://api.nextscenes.org";
}

export const API_BASE = computeApiBase();

function buildUrl(path) {
  const p = String(path || "");
  const cleanPath = `${p.startsWith("/") ? "" : "/"}${p}`;
  return `${API_BASE}${cleanPath}`;
}

function shouldSendBody(method) {
  return method !== "GET" && method !== "HEAD";
}

function isUnsafeMethod(method) {
  const m = String(method || "GET").toUpperCase();
  return m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE";
}

function isAuthPath(path) {
  const p = String(path || "");
  return p === "/api/auth" || p.startsWith("/api/auth/");
}

function isContactPath(path) {
  const p = String(path || "");
  return p === "/api/contact" || p.startsWith("/api/contact/");
}

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/* ------------------ CSRF (HttpOnly-safe) ------------------ */

let cachedCsrfToken = "";
let csrfFetchedAtMs = 0;
let csrfFetchPromise = null;

async function fetchCsrfFromServer() {
  try {
    const res = await fetch(buildUrl("/api/csrf"), {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    const data = await readJsonSafe(res);
    const token = String(data?.token || "").trim();

    if (token) {
      cachedCsrfToken = token;
      csrfFetchedAtMs = Date.now();
      return cachedCsrfToken;
    }

    cachedCsrfToken = "";
    csrfFetchedAtMs = Date.now();
    return "";
  } catch {
    // IMPORTANT: never throw here (prevents "Failed to fetch" chain reaction)
    return "";
  }
}

async function ensureCsrfToken(forceRefresh = false) {
  if (!forceRefresh) {
    const age = Date.now() - csrfFetchedAtMs;
    if (cachedCsrfToken && age < 10 * 60 * 1000) return cachedCsrfToken;
  }

  if (csrfFetchPromise) return await csrfFetchPromise;

  csrfFetchPromise = fetchCsrfFromServer();

  try {
    return await csrfFetchPromise;
  } finally {
    csrfFetchPromise = null;
  }
}

function looksLikeCsrfFailure(status, data, msg) {
  if (status !== 403) return false;
  const m = String(msg || data?.message || data?.error || "").toLowerCase();
  return m.includes("csrf");
}

/* ------------------ request ------------------ */

async function requestOnce(path, options = {}) {
  const url = buildUrl(path);
  const method = String(options.method || "GET").toUpperCase();

  // do not mutate caller options/headers
  const headers = { ...(options.headers || {}) };

  const hasBody = options.body !== undefined && options.body !== null;
  const canHaveBody = shouldSendBody(method);

  let body = undefined;

  if (canHaveBody && hasBody) {
    // Respect FormData / Blob bodies (do not JSON stringify)
    const isFormData =
      typeof FormData !== "undefined" && options.body instanceof FormData;
    const isBlob = typeof Blob !== "undefined" && options.body instanceof Blob;

    if (isFormData || isBlob) {
      body = options.body;
      // Let browser set multipart boundary / blob type
    } else if (typeof options.body === "string") {
      body = options.body;
      // default JSON for string bodies unless explicitly overridden
      if (!headers["Content-Type"]) headers["Content-Type"] = "application/json";
    } else {
      body = JSON.stringify(options.body);
      if (!headers["Content-Type"]) headers["Content-Type"] = "application/json";
    }
  }

  // CSRF header for unsafe requests (skip only csrf bootstrap + public contact)
  if (
    isUnsafeMethod(method) &&
    !isContactPath(path) &&
    path !== "/api/csrf"
  ) {
    const csrf = await ensureCsrfToken(false);
    if (csrf && !headers["X-CSRF-Token"]) headers["X-CSRF-Token"] = csrf;
  }

  const res = await fetch(url, {
    ...options,
    method,
    credentials: "include",
    headers,
    ...(body !== undefined ? { body } : {}),
  });

  const data = await readJsonSafe(res);

  if (!res.ok) {
    const msg =
      (data && (data.message || data.error)) ||
      `${res.status} ${res.statusText}` ||
      "Request failed";

    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

async function request(path, options = {}) {
  try {
    return await requestOnce(path, options);
  } catch (err) {
    const status = err?.status;
    const data = err?.data;
    const msg = err?.message;

    // Retry once on true CSRF failure (all unsafe routes except public contact)
    if (!isContactPath(path) && looksLikeCsrfFailure(status, data, msg)) {
      await ensureCsrfToken(true);

      const headers = { ...(options.headers || {}) };
      if (cachedCsrfToken) headers["X-CSRF-Token"] = cachedCsrfToken;

      const opts2 = { ...(options || {}), headers };
      return await requestOnce(path, opts2);
    }

    throw err;
  }
}

export function api(path, options) {
  return request(path, options || {});
}

api.get = (path) => request(path, { method: "GET" });

api.post = (path, body, options = {}) =>
  request(path, {
    ...(options || {}),
    method: "POST",
    body: body || {},
  });

api.put = (path, body, options = {}) =>
  request(path, {
    ...(options || {}),
    method: "PUT",
    body: body || {},
  });

api.patch = (path, body, options = {}) =>
  request(path, {
    ...(options || {}),
    method: "PATCH",
    body: body || {},
  });

api.del = (path) =>
  request(path, {
    method: "DELETE",
  });

api.delBody = (path, body) =>
  request(path, {
    method: "DELETE",
    body: body || {},
  });

api.uploadStoryCover = async (file) => {
  if (!file) throw new Error("Missing cover image file.");
  const form = new FormData();
  form.append("cover", file);
  return await request("/api/stories/uploads/story-cover", {
    method: "POST",
    body: form,
  });
};

api.uploadSceneIllustration = async (file) => {
  if (!file) throw new Error("Missing scene illustration image file.");
  const form = new FormData();
  form.append("illustration", file);
  return await request("/api/stories/uploads/scene-illustration", {
    method: "POST",
    body: form,
  });
};

api.uploadAuthorPhoto = async (file) => {
  if (!file) throw new Error("Missing author photo file.");
  const form = new FormData();
  form.append("avatar", file);
  return await request("/api/profile/uploads/avatar", {
    method: "POST",
    body: form,
  });
};

api.uploadMysteryImage = async (file) => {
  if (!file) throw new Error("Missing Mystery250 image file.");
  const form = new FormData();
  form.append("image", file);
  return await request("/api/mysteries/uploads/image", {
    method: "POST",
    body: form,
  });
};

api.uploadMysteryVideo = async (file) => {
  if (!file) throw new Error("Missing Mystery250 video file.");
  const form = new FormData();
  form.append("video", file);
  return await request("/api/mysteries/uploads/video", {
    method: "POST",
    body: form,
  });
};

api.reviewSceneIllustration = async (storyId, sceneId, payload = {}) => {
  const sid = String(storyId || "").trim();
  const scid = String(sceneId || "").trim();
  if (!sid) throw new Error("Missing story id.");
  if (!scid) throw new Error("Missing scene id.");

  return await request(
    `/api/stories/${encodeURIComponent(sid)}/scenes/${encodeURIComponent(scid)}/illustration`,
    {
      method: "PATCH",
      body: payload || {},
    }
  );
};

api.approveSceneIllustration = async (storyId, sceneId) => {
  return await api.reviewSceneIllustration(storyId, sceneId, { action: "approve" });
};

api.rejectSceneIllustration = async (storyId, sceneId, opts = {}) => {
  return await api.reviewSceneIllustration(storyId, sceneId, {
    action: "reject",
    illustrationReviewReason: opts.reason || opts.illustrationReviewReason || "",
    illustrationReviewNote: opts.note || opts.illustrationReviewNote || "",
  });
};

api.clearSceneIllustration = async (storyId, sceneId) => {
  return await api.reviewSceneIllustration(storyId, sceneId, { action: "clear" });
};

api.replaceSceneIllustration = async (storyId, sceneId, payload = {}) => {
  const sid = String(storyId || "").trim();
  const scid = String(sceneId || "").trim();
  if (!sid) throw new Error("Missing story id.");
  if (!scid) throw new Error("Missing scene id.");

  const cleanPayload = {
    action: "replace",
    ...payload,
  };

  const attempts = [
    () =>
      request(`/api/stories/${encodeURIComponent(sid)}/scenes/${encodeURIComponent(scid)}/illustration`, {
        method: "PATCH",
        body: cleanPayload,
      }),
    () =>
      request(`/api/stories/${encodeURIComponent(sid)}/scenes/${encodeURIComponent(scid)}`, {
        method: "PATCH",
        body: cleanPayload,
      }),
  ];

  let lastError = null;
  for (const attempt of attempts) {
    try {
      return await attempt();
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Scene illustration replacement failed.");
};

/* -------------------- NextScenes helpers -------------------- */

function clampInt(n, min, max, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  const v = Math.floor(x);
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

function qsFrom(obj) {
  const params = new URLSearchParams();
  Object.keys(obj || {}).forEach((k) => {
    const v = obj[k];
    if (v === undefined || v === null || v === "") return;
    params.set(k, String(v));
  });
  const s = params.toString();
  return s ? `?${s}` : "";
}

api.getStoriesList = async (opts = {}) => {
  const page = clampInt(opts.page, 1, 1000000, 1);
  const limit = clampInt(opts.limit, 1, 100, 30);

  const query = qsFrom({
    mode: "list",
    page,
    limit,
  });

  return await request(`/api/stories${query}`, { method: "GET" });
};

api.getProjectHubProjects = async (opts = {}) => {
  const rawLanguage =
    String(
      opts.language || ""
    )
      .trim()
      .toLowerCase();

  const language =
    rawLanguage === "fr"
      ? "fr"
      : "en";

  const query =
    qsFrom({
      language,
    });

  return await request(
    `/api/stories/project-hub${query}`,
    {
      method: "GET",
    }
  );
};

/* ------------------ Children's Book persistence pilot ------------------ */

api.prepareChildrensBookLocalDraftReflow = async ({
  draft = {},
  changedUnitId = "",
  proposedUnit = null,
  designMarginsInches = null,
  authoringImageGeometry = null,
} = {}) => {
  const normalizedChangedUnitId =
    String(
      changedUnitId || ""
    ).trim();

  if (
    !draft ||
    typeof draft !==
      "object" ||
    Array.isArray(
      draft
    )
  ) {
    throw new Error(
      "The current Children's Book authoring draft is required."
    );
  }

  if (!normalizedChangedUnitId) {
    throw new Error(
      "The changed Book Structure unit identifier is required."
    );
  }

  if (
    !proposedUnit ||
    typeof proposedUnit !==
      "object" ||
    Array.isArray(
      proposedUnit
    )
  ) {
    throw new Error(
      "The proposed Book Structure unit is required."
    );
  }

  const body = {
    draft,

    changedUnitId:
      normalizedChangedUnitId,

    proposedUnit,
  };

  if (
    designMarginsInches != null
  ) {
    if (
      typeof designMarginsInches !==
        "object" ||
      Array.isArray(
        designMarginsInches
      )
    ) {
      throw new Error(
        "Authoring design margins must be supplied as an object."
      );
    }

    body.designMarginsInches =
      designMarginsInches;
  }

  if (
    authoringImageGeometry != null
  ) {
    if (
      typeof authoringImageGeometry !==
        "object" ||
      Array.isArray(
        authoringImageGeometry
      )
    ) {
      throw new Error(
        "Authoring image geometry must be supplied as an object."
      );
    }

    body.authoringImageGeometry =
      authoringImageGeometry;
  }

  return await api.post(
    "/api/projects/childrens-book/authoring/local-draft-reflow-proposal",
    body
  );
};


api.createTextbookProject = async ({
  title = "",
  primaryLanguage = "en",
  writingMode = "solo",
  inviteEmails = [],
} = {}) => {
  const normalizedInviteEmails =
    Array.isArray(inviteEmails)
      ? inviteEmails
          .map((email) =>
            String(email || "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      : [];

  return await api.post(
    "/api/projects/textbook",
    {
      title:
        String(title || "")
          .trim(),

      primaryLanguage:
        String(
          primaryLanguage ||
            "en"
        )
          .trim()
          .toLowerCase(),

      writingMode:
        String(
          writingMode ||
            "solo"
        )
          .trim()
          .toLowerCase(),

      inviteEmails:
        normalizedInviteEmails,
    }
  );
};

api.getTextbookProjects = async ({
  status = "",
} = {}) => {
  const normalizedStatus =
    String(
      status || ""
    )
      .trim()
      .toLowerCase();

  const query =
    normalizedStatus
      ? `?status=${encodeURIComponent(
          normalizedStatus
        )}`
      : "";

  return await api.get(
    `/api/projects/textbook${query}`
  );
};


api.archiveTextbookProject = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      id
    )}/archive`,
    {}
  );
};


api.restoreTextbookProject = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      id
    )}/restore`,
    {}
  );
};

api.getTextbookCollaborativeProjects = async () => {
  return await api.get(
    "/api/projects/textbook/collaborative-projects"
  );
};

api.requestTextbookCollaborativeMembership = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      id
    )}/join-requests`,
    {}
  );
};

api.getTextbookJoinRequests = async (
  projectId,
  {
    status = "pending",
  } = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  const params =
    new URLSearchParams();

  const normalizedStatus =
    String(status || "")
      .trim()
      .toLowerCase();

  if (normalizedStatus) {
    params.set(
      "status",
      normalizedStatus
    );
  }

  const query =
    params.toString();

  return await api.get(
    `/api/projects/textbook/${encodeURIComponent(
      id
    )}/join-requests${
      query
        ? `?${query}`
        : ""
    }`
  );
};

api.decideTextbookJoinRequest = async (
  projectId,
  requestId,
  action
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const request =
    String(
      requestId || ""
    ).trim();

  const decision =
    String(
      action || ""
    )
      .trim()
      .toLowerCase();

  if (
    !project ||
    !request
  ) {
    throw new Error(
      "A Textbook project and join request are required."
    );
  }

  if (
    decision !== "approve" &&
    decision !== "reject"
  ) {
    throw new Error(
      "Join-request action must be approve or reject."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/join-requests/${encodeURIComponent(
      request
    )}/decision`,
    {
      action:
        decision,
    }
  );
};

api.getTextbookProject = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/textbook/${encodeURIComponent(
      id
    )}`
  );
};

api.saveTextbookProjectPlan = async (
  projectId,
  {
    overview,
    formatPlan,
    bookMap,
    publicationPlan,
    expectedRevision,
  } = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  const revision =
    Number(
      expectedRevision
    );

  if (
    !Number.isInteger(
      revision
    ) ||
    revision < 1
  ) {
    throw new Error(
      "A valid Textbook Project Plan revision is required."
    );
  }

  const projectPlan = {};

  if (overview !== undefined) {
    if (
      !overview ||
      typeof overview !==
        "object" ||
      Array.isArray(
        overview
      )
    ) {
      throw new Error(
        "A valid Textbook Project Overview change is required."
      );
    }

    projectPlan.overview =
      overview;
  }

  if (formatPlan !== undefined) {
    if (
      !formatPlan ||
      typeof formatPlan !==
        "object" ||
      Array.isArray(
        formatPlan
      )
    ) {
      throw new Error(
        "A valid Textbook Format & Page Design change is required."
      );
    }

    projectPlan.formatPlan =
      formatPlan;
  }

  if (publicationPlan !== undefined) {
    if (
      !publicationPlan ||
      typeof publicationPlan !== "object" ||
      Array.isArray(publicationPlan)
    ) {
      throw new Error(
        "A valid Textbook Publication Plan is required."
      );
    }

    projectPlan.publicationPlan =
      publicationPlan;
  }

  if (bookMap !== undefined) {
    if (
      !bookMap ||
      typeof bookMap !==
        "object" ||
      Array.isArray(
        bookMap
      )
    ) {
      throw new Error(
        "A valid Textbook Book Map change is required."
      );
    }

    projectPlan.bookMap =
      bookMap;
  }

  if (
    Object.keys(projectPlan)
      .length === 0
  ) {
    throw new Error(
      "A Textbook Project Plan change is required."
    );
  }

  return await api.put(
    `/api/projects/textbook/${encodeURIComponent(
      id
    )}/project-plan`,
    {
      expectedRevision:
        revision,
      projectPlan,
    }
  );
};



api.getTextbookChapterDraft = async (
  projectId,
  chapterItemId
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (!chapter) {
    throw new Error(
      "A Textbook chapter identifier is required."
    );
  }

  return await api.get(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}`
  );
};


api.submitTextbookChapterProposal = async (
  projectId,
  chapterItemId,
  proposalRoundId,
  proposal = {},
  submissionKey
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  const key =
    String(
      submissionKey || ""
    ).trim();

  const expectedChapterRevision =
    Number(
      proposal
        ?.expectedChapterRevision
    );

  const proposalText =
    String(
      proposal
        ?.proposalText || ""
    ).trim();

  const proposalManuscriptContent =
    proposal
      ?.proposalManuscriptContent;

  if (
    !project ||
    !chapter ||
    !roundId
  ) {
    throw new Error(
      "A Textbook project, chapter, and Proposal Round are required."
    );
  }

  if (!key) {
    throw new Error(
      "A Textbook proposal submission key is required."
    );
  }

  if (
    !Number.isInteger(
      expectedChapterRevision
    ) ||
    expectedChapterRevision < 1
  ) {
    throw new Error(
      "A valid saved Chapter revision is required for a proposal."
    );
  }

  if (!proposalText) {
    throw new Error(
      "Write your proposal before submitting it."
    );
  }

  return await request(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/proposal-round/${encodeURIComponent(
      roundId
    )}/contributions`,
    {
      method: "POST",
      headers: {
        "Idempotency-Key":
          key,
      },
      body:
        JSON.stringify({
          expectedChapterRevision,
          proposalText,

          ...(
            proposalManuscriptContent &&
            typeof proposalManuscriptContent ===
              "object" &&
            !Array.isArray(
              proposalManuscriptContent
            )
              ? {
                  proposalManuscriptContent,
                }
              : {}
          ),
        }),
    }
  );
};


api.openTextbookChapterProposalRound = async (
  projectId,
  chapterItemId,
  request = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (!chapter) {
    throw new Error(
      "A Textbook chapter identifier is required."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/proposal-round/open`,
    request || {}
  );
};


api.closeTextbookChapterProposalRound = async (
  projectId,
  chapterItemId,
  proposalRoundId,
  request = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  if (
    !project ||
    !chapter ||
    !roundId
  ) {
    throw new Error(
      "A Textbook project, chapter, and Proposal Round are required."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/proposal-round/${encodeURIComponent(
      roundId
    )}/close`,
    request || {}
  );
};



api.decideTextbookChapterProposal = async (
  projectId,
  chapterItemId,
  proposalRoundId,
  contributionId,
  request = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  const contribution =
    String(
      contributionId || ""
    ).trim();

  const action =
    String(
      request?.action || ""
    )
      .trim()
      .toLowerCase();

  const expectedRoundRevision =
    Number(
      request
        ?.expectedRoundRevision
    );

  const expectedChapterRevision =
    Number(
      request
        ?.expectedChapterRevision
    );

  if (
    !project ||
    !chapter ||
    !roundId ||
    !contribution
  ) {
    throw new Error(
      "A Textbook project, chapter, Proposal Round, and proposal are required."
    );
  }

  if (
    action !== "accept" &&
    action !== "reject"
  ) {
    throw new Error(
      "Choose Accept This Proposal or Reject This Proposal."
    );
  }

  if (
    !Number.isInteger(
      expectedRoundRevision
    ) ||
    expectedRoundRevision < 1 ||
    !Number.isInteger(
      expectedChapterRevision
    ) ||
    expectedChapterRevision < 1
  ) {
    throw new Error(
      "Reload the Textbook chapter before deciding this proposal."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/proposal-round/${encodeURIComponent(
      roundId
    )}/contributions/${encodeURIComponent(
      contribution
    )}/decision`,
    {
      action,
      expectedRoundRevision,
      expectedChapterRevision,
    }
  );
};


api.rejectAllTextbookChapterProposals = async (
  projectId,
  chapterItemId,
  proposalRoundId,
  request = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  const expectedRoundRevision =
    Number(
      request
        ?.expectedRoundRevision
    );

  const expectedChapterRevision =
    Number(
      request
        ?.expectedChapterRevision
    );

  if (
    !project ||
    !chapter ||
    !roundId
  ) {
    throw new Error(
      "A Textbook project, chapter, and Proposal Round are required."
    );
  }

  if (
    !Number.isInteger(
      expectedRoundRevision
    ) ||
    expectedRoundRevision < 1 ||
    !Number.isInteger(
      expectedChapterRevision
    ) ||
    expectedChapterRevision < 1
  ) {
    throw new Error(
      "Reload the Textbook chapter before rejecting all proposals."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/proposal-round/${encodeURIComponent(
      roundId
    )}/decision`,
    {
      action:
        "reject_all",
      expectedRoundRevision,
      expectedChapterRevision,
    }
  );
};


api.getTextbookBookSnapshots = async (
  projectId
) => {
  const project =
    String(projectId || "").trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/book-snapshots`
  );
};

api.getTextbookBookSnapshotPreview = async (
  projectId,
  snapshotId
) => {
  const project =
    String(projectId || "").trim();
  const snapshot =
    String(snapshotId || "").trim();

  if (!project || !snapshot) {
    throw new Error(
      "A Textbook project and saved version are required."
    );
  }

  return await api.get(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/book-snapshots/${encodeURIComponent(
      snapshot
    )}`
  );
};


api.lockTextbookPublicationManuscript = async (
  projectId,
  expectedRevision,
  reviewAssembly = null,
  chapterBlockSelections = []
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const revision =
    Number(
      expectedRevision
    );

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (
    !Number.isInteger(
      revision
    ) ||
    revision < 1
  ) {
    throw new Error(
      "Reload the Textbook before locking its publication manuscript."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/publication-manuscript/lock`,
    {
      expectedRevision:
        revision,
      reviewAssembly,
      chapterBlockSelections:
        Array.isArray(
          chapterBlockSelections
        )
          ? chapterBlockSelections
          : [],
    }
  );
};


api.saveTextbookChapterDraft = async (
  projectId,
  chapterItemId,
  {
    blocks,
    sectionLayout = [],
    expectedRevision,
  } = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (!chapter) {
    throw new Error(
      "A Textbook chapter identifier is required."
    );
  }

  if (
    expectedRevision ===
      null ||
    expectedRevision ===
      undefined ||
    typeof expectedRevision ===
      "boolean" ||
    (
      typeof expectedRevision ===
        "string" &&
      !expectedRevision.trim()
    )
  ) {
    throw new Error(
      "A valid Textbook Chapter Draft revision is required."
    );
  }

  const revision =
    Number(
      expectedRevision
    );

  if (
    !Number.isInteger(
      revision
    ) ||
    revision < 0
  ) {
    throw new Error(
      "A valid Textbook Chapter Draft revision is required."
    );
  }

  if (!Array.isArray(blocks)) {
    throw new Error(
      "A Textbook Chapter Draft blocks array is required."
    );
  }

  if (
    !Array.isArray(
      sectionLayout
    )
  ) {
    throw new Error(
      "A Textbook Chapter Draft sectionLayout array is required."
    );
  }

  return await api.put(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}`,
    {
      expectedRevision:
        revision,

      chapterDraft: {
        blocks,
        sectionLayout,
      },
    }
  );
};

api.discardTextbookChapterDraft = async (
  projectId,
  chapterItemId,
  expectedRevision
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (!chapter) {
    throw new Error(
      "A Textbook chapter identifier is required."
    );
  }

  if (
    expectedRevision ===
      null ||
    expectedRevision ===
      undefined ||
    typeof expectedRevision ===
      "boolean" ||
    (
      typeof expectedRevision ===
        "string" &&
      !expectedRevision.trim()
    )
  ) {
    throw new Error(
      "A saved Textbook Chapter Draft revision is required."
    );
  }

  const revision =
    Number(
      expectedRevision
    );

  if (
    !Number.isInteger(
      revision
    ) ||
    revision < 1
  ) {
    throw new Error(
      "A saved Textbook Chapter Draft revision is required."
    );
  }

  return await api.delBody(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}`,
    {
      expectedRevision:
        revision,
    }
  );
};


api.getTextbookFigureAssets = async (
  projectId,
  chapterItemId
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (!chapter) {
    throw new Error(
      "A Textbook chapter identifier is required."
    );
  }

  return await api.get(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/figures`
  );
};

api.createTextbookFigureAsset = async (
  projectId,
  chapterItemId,
  figureAsset = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (!chapter) {
    throw new Error(
      "A Textbook chapter identifier is required."
    );
  }

  if (
    !figureAsset ||
    typeof figureAsset !==
      "object" ||
    Array.isArray(
      figureAsset
    )
  ) {
    throw new Error(
      "Textbook Figure details are required."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/figures`,
    {
      figureAsset,
    }
  );
};

api.updateTextbookFigureAsset = async (
  projectId,
  chapterItemId,
  figureAssetId,
  {
    expectedRevision,
    figureAsset,
  } = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  const figureId =
    String(
      figureAssetId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (!chapter) {
    throw new Error(
      "A Textbook chapter identifier is required."
    );
  }

  if (!figureId) {
    throw new Error(
      "A Textbook Figure identifier is required."
    );
  }

  const revision =
    Number(
      expectedRevision
    );

  if (
    !Number.isInteger(
      revision
    ) ||
    revision < 1
  ) {
    throw new Error(
      "A current Textbook Figure revision is required."
    );
  }

  if (
    !figureAsset ||
    typeof figureAsset !==
      "object" ||
    Array.isArray(
      figureAsset
    )
  ) {
    throw new Error(
      "Textbook Figure changes are required."
    );
  }

  const metadata = {
    figureType:
      String(
        figureAsset.figureType ||
        ""
      ).trim(),

    caption:
      figureAsset.caption == null
        ? ""
        : String(
            figureAsset.caption
          ),

    alternativeText:
      figureAsset.alternativeText ==
        null
        ? ""
        : String(
            figureAsset
              .alternativeText
          ),

    creatorOrSource:
      figureAsset.creatorOrSource ==
        null
        ? ""
        : String(
            figureAsset
              .creatorOrSource
          ),

    rightsOrLicense:
      figureAsset.rightsOrLicense ==
        null
        ? ""
        : String(
            figureAsset
              .rightsOrLicense
          ),
  };

  return await api.put(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/figures/${encodeURIComponent(
      figureId
    )}`,
    {
      expectedRevision:
        revision,

      figureAsset:
        metadata,
    }
  );
};


api.getTextbookPublicationAssets = async (
  projectId
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/publication-assets`
  );
};

api.uploadTextbookPublicationCover = async (
  projectId,
  purpose,
  file,
  {
    creatorOrSource = "",
    rightsOrLicense = "",
    rightsConfirmed = false,
    expectedRevision = 0,
  } = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const normalizedPurpose =
    String(
      purpose || ""
    )
      .trim()
      .toLowerCase();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (
    ![
      "front_cover",
      "back_cover",
    ].includes(
      normalizedPurpose
    )
  ) {
    throw new Error(
      "Choose a valid Textbook cover."
    );
  }

  if (!file) {
    throw new Error(
      "Choose a cover image first."
    );
  }

  const mimeType =
    String(
      file?.type || ""
    )
      .trim()
      .toLowerCase();

  if (
    mimeType &&
    ![
      "image/jpeg",
      "image/png",
      "image/webp",
    ].includes(
      mimeType
    )
  ) {
    throw new Error(
      "Choose a JPG, PNG, or WEBP cover image."
    );
  }

  if (
    Number(
      file?.size || 0
    ) >
      5 * 1024 * 1024
  ) {
    throw new Error(
      "Cover image must be 5 MB or smaller."
    );
  }

  const form =
    new FormData();

  form.append(
    "cover",
    file
  );

  form.append(
    "creatorOrSource",
    String(
      creatorOrSource || ""
    )
  );

  form.append(
    "rightsOrLicense",
    String(
      rightsOrLicense || ""
    )
  );

  form.append(
    "rightsConfirmed",
    rightsConfirmed === true
      ? "true"
      : "false"
  );

  if (
    Number.isInteger(
      Number(
        expectedRevision
      )
    ) &&
    Number(
      expectedRevision
    ) > 0
  ) {
    form.append(
      "expectedRevision",
      String(
        Number(
          expectedRevision
        )
      )
    );
  }

  return await request(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/publication-assets/${encodeURIComponent(
      normalizedPurpose
    )}/upload`,
    {
      method: "POST",
      body: form,
    }
  );
};

api.uploadTextbookFigureAsset = async (
  projectId,
  chapterItemId,
  file,
  figureAsset = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const chapter =
    String(
      chapterItemId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  if (!chapter) {
    throw new Error(
      "A Textbook chapter identifier is required."
    );
  }

  if (!file) {
    throw new Error(
      "Choose a Figure image first."
    );
  }

  const mimeType =
    String(
      file?.type || ""
    )
      .trim()
      .toLowerCase();

  if (
    mimeType &&
    ![
      "image/jpeg",
      "image/png",
      "image/webp",
    ].includes(
      mimeType
    )
  ) {
    throw new Error(
      "Choose a JPG, PNG, or WEBP Figure image."
    );
  }

  if (
    Number(
      file?.size || 0
    ) >
    5 * 1024 * 1024
  ) {
    throw new Error(
      "Figure image must be 5 MB or smaller."
    );
  }

  const metadata =
    (
      figureAsset &&
      typeof figureAsset ===
        "object" &&
      !Array.isArray(
        figureAsset
      )
    )
      ? figureAsset
      : {};

  const form =
    new FormData();

  form.append(
    "figure",
    file
  );

  [
    "figureType",
    "sectionItemId",
    "caption",
    "alternativeText",
    "creatorOrSource",
    "rightsOrLicense",
  ].forEach(
    (field) => {
      form.append(
        field,
        String(
          metadata[field] ??
          ""
        )
      );
    }
  );

  return await request(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/chapters/${encodeURIComponent(
      chapter
    )}/figures/upload`,
    {
      method: "POST",
      body: form,
    }
  );
};

api.getTextbookFigureFile = async (
  fileUrl
) => {
  const rawUrl =
    String(
      fileUrl || ""
    ).trim();

  if (!rawUrl) {
    throw new Error(
      "Textbook Figure file address is required."
    );
  }

  let requestPath =
    rawUrl;

  try {
    const parsed =
      new URL(
        rawUrl,
        "http://nextscenes.local"
      );

    if (
      parsed.pathname.startsWith(
        "/api/"
      )
    ) {
      requestPath =
        `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    requestPath =
      rawUrl;
  }

  const target =
    /^https?:\/\//i.test(
      requestPath
    )
      ? requestPath
      : buildUrl(
          requestPath
        );

  const response =
    await fetch(
      target,
      {
        method: "GET",
        credentials: "include",
      }
    );

  if (!response.ok) {
    let message =
      `Could not load the Textbook Figure (${response.status}).`;

    try {
      const data =
        await readJsonSafe(
          response.clone()
        );

      message =
        data?.message ||
        data?.error ||
        message;
    } catch {
      // Preserve the controlled fallback message.
    }

    const error =
      new Error(message);

    error.status =
      response.status;

    throw error;
  }

  const blob =
    await response.blob();

  if (
    !blob ||
    Number(
      blob.size
    ) <= 0
  ) {
    throw new Error(
      "The Textbook Figure image is unavailable."
    );
  }

  return blob;
};


api.getTextbookPublicationAssetFile =
  api.getTextbookFigureFile;


api.approveTextbookInterior = async (
  projectId,
  approval = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const productionRunId =
    String(
      approval
        ?.productionRunId ||
      ""
    ).trim();

  if (
    !project ||
    !productionRunId
  ) {
    throw new Error(
      "Textbook Interior Approval requires a project and production-run identifier."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/production-run/${encodeURIComponent(
      productionRunId
    )}/interior-approval`,
    {
      creatorConfirmed:
        true,

      pageCount:
        Number(
          approval
            ?.pageCount ||
          0
        ),

      contentSha256:
        String(
          approval
            ?.contentSha256 ||
          ""
        ).trim(),

      measuredInteriorFingerprint:
        String(
          approval
            ?.measuredInteriorFingerprint ||
          ""
        ).trim(),

      reviewCandidateFingerprint:
        String(
          approval
            ?.reviewCandidateFingerprint ||
          ""
        ).trim(),
    }
  );
};


api.getTextbookCurrentProductionRun = async (
  projectId
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "Textbook current production state requires a project identifier."
    );
  }

  return await api(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/production-run/current`,
    {
      method:
        "GET",

      /*
       * V1A.2 — force a fresh JSON continuity response.
       * This prevents conditional-cache 304 responses from
       * stripping the body Publication Preparation needs.
       */
      cache:
        "no-store",

      headers: {
        Accept:
          "application/json",
      },
    }
  );
};


api.getTextbookFinalCoverGeometry = async (
  projectId,
  productionRunId
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const run =
    String(
      productionRunId || ""
    ).trim();

  if (!project || !run) {
    throw new Error(
      "Textbook Final Cover geometry requires a project and approved production-run identifier."
    );
  }

  return await api.get(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/production-run/${encodeURIComponent(
      run
    )}/cover-geometry`
  );
};



api.rebindTextbookCoverArtwork = async (
  projectId,
  productionRunId
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const run =
    String(
      productionRunId || ""
    ).trim();

  if (!project || !run) {
    throw new Error(
      "Textbook cover rebinding requires a project and approved production-run identifier."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/production-run/${encodeURIComponent(
      run
    )}/cover-artwork/rebind`,
    {}
  );
};


api.generateTextbookCoverReview = async (
  projectId,
  productionRunId
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const run =
    String(
      productionRunId || ""
    ).trim();

  if (!project || !run) {
    throw new Error(
      "Textbook Cover Review requires a project and approved production-run identifier."
    );
  }

  const path =
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/production-run/${encodeURIComponent(
      run
    )}/cover-review`;

  const response =
    await fetch(
      buildUrl(path),
      {
        method:
          "GET",

        credentials:
          "include",

        cache:
          "no-store",

        headers: {
          Accept:
            "application/pdf",
        },
      }
    );

  if (!response.ok) {
    const data =
      await readJsonSafe(
        response.clone()
      );

    const message =
      data?.message ||
      data?.error ||
      `Could not generate the Textbook Cover Review PDF (${response.status}).`;

    const error =
      new Error(
        message
      );

    error.status =
      response.status;

    error.data =
      data;

    throw error;
  }

  const blob =
    await response.blob();

  if (
    !blob ||
    Number(
      blob.size
    ) <= 0
  ) {
    throw new Error(
      "The Textbook Cover Review PDF is unavailable."
    );
  }

  return {
    blob,

    coverReviewReady:
      String(
        response.headers.get(
          "X-Textbook-Cover-Review-Ready"
        ) ||
        ""
      ) === "true",

    finalPageCount:
      Number(
        response.headers.get(
          "X-Textbook-Cover-Review-Final-Page-Count"
        ) ||
        0
      ),

    contentSha256:
      String(
        response.headers.get(
          "X-Textbook-Cover-Review-Content-Sha256"
        ) ||
        ""
      ),

    reviewCandidateFingerprint:
      String(
        response.headers.get(
          "X-Textbook-Cover-Review-Candidate-Fingerprint"
        ) ||
        ""
      ),

    spineTextEligible:
      String(
        response.headers.get(
          "X-Textbook-Cover-Review-Spine-Text-Eligible"
        ) ||
        ""
      ) === "true",
  };
};



api.approveTextbookCover = async (
  projectId,
  productionRunId,
  {
    finalPageCount,
    contentSha256,
    reviewCandidateFingerprint,
  } = {}
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  const run =
    String(
      productionRunId || ""
    ).trim();

  if (!project || !run) {
    throw new Error(
      "Textbook Cover Approval requires a project and approved production-run identifier."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/production-run/${encodeURIComponent(
      run
    )}/cover-approval`,
    {
      creatorConfirmed:
        true,

      finalPageCount:
        Number(
          finalPageCount ||
          0
        ),

      contentSha256:
        String(
          contentSha256 ||
          ""
        ).trim(),

      reviewCandidateFingerprint:
        String(
          reviewCandidateFingerprint ||
          ""
        ).trim(),
    }
  );
};



api.persistTextbookApprovedPublicationArtifacts = async (
  projectId,
  productionRunId
) => {
  const project = String(projectId || "").trim();
  const run = String(productionRunId || "").trim();

  if (!project || !run) {
    throw new Error(
      "Approved Textbook publication-artifact persistence requires a project and approved production-run identifier."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(project)}/production-run/${encodeURIComponent(run)}/publication-artifacts/persist`,
    {}
  );
};




api.getTextbookFinalPublicationFile = async (
  projectId,
  productionRunId,
  role
) => {
  const project =
    String(
      projectId ||
      ""
    ).trim();

  const run =
    String(
      productionRunId ||
      ""
    ).trim();

  const normalizedRole =
    String(
      role ||
      ""
    )
      .trim()
      .toLowerCase();

  if (
    !project ||
    !run ||
    (
      normalizedRole !==
        "interior" &&
      normalizedRole !==
        "cover"
    )
  ) {
    throw new Error(
      "Final Textbook publication-file review requires a project, approved production run, and valid file role."
    );
  }

  const endpoint =
    normalizedRole ===
      "interior"
      ? "final-interior"
      : "final-cover";

  const response =
    await fetch(
      buildUrl(
        `/api/projects/textbook/${encodeURIComponent(
          project
        )}/production-run/${encodeURIComponent(
          run
        )}/publication-files/${endpoint}`
      ),
      {
        method:
          "GET",

        credentials:
          "include",

        cache:
          "no-store",

        headers: {
          Accept:
            "application/pdf",
        },
      }
    );

  if (!response.ok) {
    const data =
      await readJsonSafe(
        response.clone()
      );

    const message =
      data?.message ||
      data?.error ||
      `Could not load the Final Textbook publication PDF (${response.status}).`;

    const error =
      new Error(
        message
      );

    error.status =
      response.status;

    error.data =
      data;

    throw error;
  }

  const blob =
    await response.blob();

  if (
    !blob ||
    Number(
      blob.size
    ) <= 0
  ) {
    throw new Error(
      "The Final Textbook publication PDF is unavailable."
    );
  }

  return {
    blob,

    role:
      normalizedRole,

    fileName:
      normalizedRole ===
        "interior"
        ? "nextscenes-textbook-final-interior.pdf"
        : "nextscenes-textbook-final-cover.pdf",
  };
};



api.buildTextbookFinalPublicationPackage = async (
  projectId,
  productionRunId
) => {
  const project = String(projectId || "").trim();
  const run = String(productionRunId || "").trim();

  if (!project || !run) {
    throw new Error(
      "Textbook Final Publication Package requires a project and approved production-run identifier."
    );
  }

  return await api.post(
    `/api/projects/textbook/${encodeURIComponent(project)}/production-run/${encodeURIComponent(run)}/publication-package/build`,
    {}
  );
};


api.generateTextbookInteriorReview = async (
  projectId
) => {
  const project =
    String(
      projectId || ""
    ).trim();

  if (!project) {
    throw new Error(
      "A Textbook project identifier is required."
    );
  }

  const path =
    `/api/projects/textbook/${encodeURIComponent(
      project
    )}/production-run/interior-review`;

  async function attempt(
    forceCsrf
  ) {
    const csrf =
      await ensureCsrfToken(
        forceCsrf
      );

    const headers = {
      Accept:
        "application/pdf",
      "Content-Type":
        "application/json",
    };

    if (csrf) {
      headers[
        "X-CSRF-Token"
      ] = csrf;
    }

    const response =
      await fetch(
        buildUrl(path),
        {
          method:
            "POST",
          credentials:
            "include",
          headers,
          body:
            JSON.stringify({}),
        }
      );

    if (!response.ok) {
      const data =
        await readJsonSafe(
          response.clone()
        );

      const message =
        data?.message ||
        data?.error ||
        `Could not generate the Textbook Interior Review PDF (${response.status}).`;

      const error =
        new Error(
          message
        );

      error.status =
        response.status;

      error.data =
        data;

      throw error;
    }

    const blob =
      await response.blob();

    if (
      !blob ||
      Number(
        blob.size
      ) <= 0
    ) {
      throw new Error(
        "The Textbook Interior Review PDF is unavailable."
      );
    }

    return {
      blob,

      pageCount:
        Number(
          response.headers.get(
            "X-Textbook-Interior-Page-Count"
          ) ||
          0
        ),

      productionRunId:
        String(
          response.headers.get(
            "X-Textbook-Production-Run-Id"
          ) ||
          ""
        ),

      contentSha256:
        String(
          response.headers.get(
            "X-Textbook-Interior-Content-Sha256"
          ) ||
          ""
        ),

      measuredInteriorFingerprint:
        String(
          response.headers.get(
            "X-Textbook-Measured-Interior-Fingerprint"
          ) ||
          ""
        ),

      reviewCandidateFingerprint:
        String(
          response.headers.get(
            "X-Textbook-Interior-Review-Candidate-Fingerprint"
          ) ||
          ""
        ),

      creatorApprovalStillRequired:
        String(
          response.headers.get(
            "X-Textbook-Creator-Approval-Required"
          ) ||
          ""
        ) === "true",

      finalPageCountLocked:
        String(
          response.headers.get(
            "X-Textbook-Final-Page-Count-Locked"
          ) ||
          ""
        ) === "true",

      approvalReady:
        String(
          response.headers.get(
            "X-Textbook-Interior-Approval-Ready"
          ) ||
          ""
        ) === "true",

      minimumPageCount:
        Number(
          response.headers.get(
            "X-Textbook-Interior-Min-Page-Count"
          ) ||
          0
        ),

      maximumPageCount:
        Number(
          response.headers.get(
            "X-Textbook-Interior-Max-Page-Count"
          ) ||
          0
        ),

      evenPageCount:
        String(
          response.headers.get(
            "X-Textbook-Interior-Even-Page-Count"
          ) ||
          ""
        ) === "true",

      withinPrintRange:
        String(
          response.headers.get(
            "X-Textbook-Interior-Within-Print-Range"
          ) ||
          ""
        ) === "true",

      approvalBlockers:
        String(
          response.headers.get(
            "X-Textbook-Interior-Approval-Blockers"
          ) ||
          ""
        )
          .split(",")
          .map((value) =>
            value.trim()
          )
          .filter(Boolean),
    };
  }

  try {
    return await attempt(
      false
    );
  } catch (error) {
    if (
      looksLikeCsrfFailure(
        error?.status,
        error?.data,
        error?.message
      )
    ) {
      return await attempt(
        true
      );
    }

    throw error;
  }
};


api.createScreenplayProject = async ({
  title = "",
  primaryLanguage = "en",
  writingMode = "solo",
  inviteEmails = [],
} = {}) => {
  const normalizedInviteEmails =
    Array.isArray(inviteEmails)
      ? inviteEmails
          .map((email) =>
            String(email || "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      : [];

  return await api.post(
    "/api/projects/screenplay",
    {
      title,
      primaryLanguage,
      writingMode,
      inviteEmails:
        normalizedInviteEmails,
    }
  );
};

api.getScreenplayProjects = async ({
  status = "",
} = {}) => {
  const normalizedStatus =
    String(status || "")
      .trim()
      .toLowerCase();

  const query =
    normalizedStatus === "archived"
      ? "?status=archived"
      : "";

  return await api.get(
    `/api/projects/screenplay${query}`
  );
};

api.getScreenplayProjectInvitations = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/invitations`
  );
};

api.resendScreenplayProjectInvitation = async (
  projectId,
  invitationId
) => {
  const id =
    String(projectId || "").trim();

  const inviteId =
    String(
      invitationId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  if (!inviteId) {
    throw new Error(
      "A Screenplay invitation identifier is required."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/invitations/${encodeURIComponent(
      inviteId
    )}/resend`,
    {}
  );
};

api.updateScreenplayProjectDetails = async (
  projectId,
  {
    title,
    primaryLanguage,
  } = {}
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  const details = {};

  if (title !== undefined) {
    details.title =
      String(title || "").trim();
  }

  if (
    primaryLanguage !==
    undefined
  ) {
    details.primaryLanguage =
      String(
        primaryLanguage || ""
      )
        .trim()
        .toLowerCase();
  }

  return await api.patch(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/details`,
    details
  );
};

api.archiveScreenplayProject = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/archive`,
    {}
  );
};

api.restoreScreenplayProject = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/restore`,
    {}
  );
};

api.getScreenplayProject = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}`
  );
};

api.getCurrentScreenplayProposalRound = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/proposal-rounds/current`
  );
};


api.openScreenplayProposalRound = async (
  projectId,
  request = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/proposal-rounds/open`,
    request || {}
  );
};


api.closeScreenplayProposalRound = async (
  projectId,
  proposalRoundId,
  request = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  if (!id || !roundId) {
    throw new Error(
      "A Screenplay project and Proposal Round are required."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/proposal-rounds/${encodeURIComponent(
      roundId
    )}/close`,
    request || {}
  );
};


api.getScreenplayProposalRoundContext = async (
  projectId,
  proposalRoundId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  if (!id || !roundId) {
    throw new Error(
      "A Screenplay project and Proposal Round are required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/proposal-rounds/${encodeURIComponent(
      roundId
    )}/context`
  );
};


api.submitScreenplayContribution = async (
  projectId,
  proposalRoundId,
  contribution,
  idempotencyKey
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  const submissionKey =
    String(
      idempotencyKey || ""
    ).trim();

  if (
    !id ||
    !roundId ||
    !submissionKey
  ) {
    throw new Error(
      "A Screenplay project, Proposal Round, and submission key are required."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/proposal-rounds/${encodeURIComponent(
      roundId
    )}/contributions`,
    contribution || {},
    {
      headers: {
        "Idempotency-Key":
          submissionKey,
      },
    }
  );
};


api.getScreenplayProposalRoundContributions = async (
  projectId,
  proposalRoundId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  if (!id || !roundId) {
    throw new Error(
      "A Screenplay project and Proposal Round are required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/proposal-rounds/${encodeURIComponent(
      roundId
    )}/contributions`
  );
};


api.decideScreenplayContribution = async (
  projectId,
  proposalRoundId,
  contributionId,
  decision
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  const contribution =
    String(
      contributionId || ""
    ).trim();

  if (
    !id ||
    !roundId ||
    !contribution
  ) {
    throw new Error(
      "A Screenplay contribution decision target is required."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/proposal-rounds/${encodeURIComponent(
      roundId
    )}/contributions/${encodeURIComponent(
      contribution
    )}/decision`,
    decision || {}
  );
};


api.rejectAllScreenplayContributions = async (
  projectId,
  proposalRoundId,
  decision
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  const roundId =
    String(
      proposalRoundId || ""
    ).trim();

  if (!id || !roundId) {
    throw new Error(
      "A Screenplay Proposal Round decision target is required."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/proposal-rounds/${encodeURIComponent(
      roundId
    )}/decision`,
    decision || {}
  );
};


api.getScreenplayScriptDraft = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/script-draft`
  );
};

api.getScreenplayApprovedDraft = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/approved-draft`
  );
};

api.approveScreenplayDraft = async (
  projectId,
  {
    expectedDraftRevision,
  } = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  const revision =
    Number(
      expectedDraftRevision
    );

  if (
    !Number.isInteger(
      revision
    ) ||
    revision < 1
  ) {
    throw new Error(
      "A valid saved Screenplay draft revision is required for approval."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/approved-draft`,
    {
      expectedDraftRevision:
        revision,
    }
  );
};


api.getScreenplayReleasedDraft = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/released-draft`
  );
};

api.getScreenplayReleasedReaderView = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/released-draft/reader-view`
  );
};


api.getPublicScreenplayReaderView = async (
  projectId
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  return await api.get(
    `/api/reader/screenplay/${encodeURIComponent(
      id
    )}`
  );
};

api.getScreenplayDiscovery = async (
  limit = 24
) => {
  const supplied =
    Number(limit);

  const safeLimit =
    Number.isFinite(supplied)
      ? Math.max(
          1,
          Math.min(
            50,
            Math.floor(
              supplied
            )
          )
        )
      : 24;

  return await api.get(
    `/api/reader/screenplay/discover?limit=${safeLimit}`
  );
};


api.downloadScreenplayPdf = async (
  projectId,
  payload = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  const path =
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/released-draft/export/pdf`;

  async function attempt(
    forceCsrf
  ) {
    const csrf =
      await ensureCsrfToken(
        forceCsrf
      );

    const headers = {
      Accept:
        "application/pdf",
      "Content-Type":
        "application/json",
    };

    if (csrf) {
      headers[
        "X-CSRF-Token"
      ] = csrf;
    }

    const res =
      await fetch(
        buildUrl(path),
        {
          method:
            "POST",
          credentials:
            "include",
          headers,
          body:
            JSON.stringify(
              payload || {}
            ),
        }
      );

    if (!res.ok) {
      const data =
        await readJsonSafe(
          res
        );

      const msg =
        (
          data &&
          (
            data.message ||
            data.error
          )
        ) ||
        `${res.status} ${res.statusText}` ||
        "Screenplay PDF export failed.";

      const error =
        new Error(msg);

      error.status =
        res.status;

      error.data =
        data;

      throw error;
    }

    const blob =
      await res.blob();

    const disposition =
      String(
        res.headers.get(
          "Content-Disposition"
        ) ||
        ""
      );

    const match =
      disposition.match(
        /filename="?([^";]+)"?/i
      );

    return {
      blob,
      fileName:
        match?.[1] ||
        "screenplay.pdf",
    };
  }

  try {
    return await attempt(
      false
    );
  } catch (error) {
    if (
      looksLikeCsrfFailure(
        error?.status,
        error?.data,
        error?.message
      )
    ) {
      return await attempt(
        true
      );
    }

    throw error;
  }
};


api.downloadScreenplayDocx = async (
  projectId,
  payload = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  const path =
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/released-draft/export/docx`;

  async function attempt(
    forceCsrf
  ) {
    const csrf =
      await ensureCsrfToken(
        forceCsrf
      );

    const headers = {
      Accept:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "Content-Type":
        "application/json",
    };

    if (csrf) {
      headers[
        "X-CSRF-Token"
      ] = csrf;
    }

    const res =
      await fetch(
        buildUrl(path),
        {
          method:
            "POST",
          credentials:
            "include",
          headers,
          body:
            JSON.stringify(
              payload || {}
            ),
        }
      );

    if (!res.ok) {
      const data =
        await readJsonSafe(
          res
        );

      const msg =
        (
          data &&
          (
            data.message ||
            data.error
          )
        ) ||
        `${res.status} ${res.statusText}` ||
        "Screenplay DOCX export failed.";

      const error =
        new Error(msg);

      error.status =
        res.status;

      error.data =
        data;

      throw error;
    }

    const blob =
      await res.blob();

    const disposition =
      String(
        res.headers.get(
          "Content-Disposition"
        ) ||
        ""
      );

    const match =
      disposition.match(
        /filename="?([^";]+)"?/i
      );

    return {
      blob,
      fileName:
        match?.[1] ||
        "screenplay.docx",
    };
  }

  try {
    return await attempt(
      false
    );
  } catch (error) {
    if (
      looksLikeCsrfFailure(
        error?.status,
        error?.data,
        error?.message
      )
    ) {
      return await attempt(
        true
      );
    }

    throw error;
  }
};


api.releaseScreenplayApprovedDraft = async (
  projectId,
  {
    expectedApprovedDraftId,
  } = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  const approvedDraftId =
    String(
      expectedApprovedDraftId ||
      ""
    ).trim();

  if (!approvedDraftId) {
    throw new Error(
      "An exact Approved Draft is required for release."
    );
  }

  return await api.post(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/released-draft`,
    {
      expectedApprovedDraftId:
        approvedDraftId,
    }
  );
};


api.saveScreenplayScriptDraft = async (
  projectId,
  {
    scriptDraft,
    expectedRevision,
  } = {}
) => {
  const id =
    String(
      projectId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  if (
    expectedRevision === null ||
    expectedRevision === undefined ||
    typeof expectedRevision ===
      "boolean" ||
    (
      typeof expectedRevision ===
        "string" &&
      !expectedRevision.trim()
    )
  ) {
    throw new Error(
      "A valid Screenplay draft revision is required."
    );
  }

  const revision =
    Number(
      expectedRevision
    );

  if (
    !Number.isInteger(
      revision
    ) ||
    revision < 0
  ) {
    throw new Error(
      "A valid Screenplay draft revision is required."
    );
  }

  if (
    !scriptDraft ||
    typeof scriptDraft !==
      "object" ||
    Array.isArray(
      scriptDraft
    ) ||
    !Array.isArray(
      scriptDraft.scenes
    )
  ) {
    throw new Error(
      "A Screenplay draft with a scenes array is required."
    );
  }

  const draft =
    scriptDraft;

  return await api.put(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/script-draft`,
    {
      expectedRevision:
        revision,
      scriptDraft:
        draft,
    }
  );
};

api.saveScreenplayProjectPlan = async (
  projectId,
  {
    overview,
    storyMap,
    expectedRevision,
  } = {}
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "A Screenplay project identifier is required."
    );
  }

  const projectPlan = {};

  if (overview !== undefined) {
    projectPlan.overview =
      overview &&
      typeof overview === "object" &&
      !Array.isArray(overview)
        ? overview
        : {};
  }

  if (storyMap !== undefined) {
    projectPlan.storyMap =
      storyMap &&
      typeof storyMap === "object" &&
      !Array.isArray(storyMap)
        ? storyMap
        : {};
  }

  if (
    Object.keys(projectPlan)
      .length === 0
  ) {
    throw new Error(
      "A Screenplay Project Plan change is required."
    );
  }

  return await api.put(
    `/api/projects/screenplay/${encodeURIComponent(
      id
    )}/project-plan`,
    {
      expectedRevision,
      projectPlan,
    }
  );
};


api.createChildrensBookProject = async (draft) => {
  return await api.post(
    "/api/projects/childrens-book",
    {
      draft: draft || {},
    }
  );
};

api.getChildrensBookProjects = async ({
  status = "",
} = {}) => {
  const params =
    new URLSearchParams();

  const cleanStatus =
    String(status || "")
      .trim()
      .toLowerCase();

  if (cleanStatus) {
    params.set(
      "status",
      cleanStatus
    );
  }

  const query =
    params.toString();

  return await api.get(
    `/api/projects/childrens-book${
      query ? `?${query}` : ""
    }`
  );
};

api.getChildrensBookProject = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}`
  );
};

api.getChildrensBookPublicationCompatibility = async (
  projectId,
  {
    paperType = "",
    interiorColor = "",
  } = {}
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  const query =
    new URLSearchParams({
      paperType:
        String(
          paperType || ""
        )
          .trim()
          .toLowerCase(),

      interiorColor:
        String(
          interiorColor || ""
        )
          .trim()
          .toLowerCase(),
    }).toString();

  return await api.get(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/publication-production/compatibility?${query}`
  );
};

api.getChildrensBookPublicationProduction = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/publication-production`
  );
};

api.getChildrensBookCoverPalettePreview = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.get(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/publication-production/cover-palette-preview`
  );
};

api.getChildrensBookPublicationInteriorReview = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  const response =
    await fetch(
      buildUrl(
        `/api/projects/childrens-book/${encodeURIComponent(
          id
        )}/publication-production/interior-review`
      ),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept:
            "application/pdf",
        },
      }
    );

  if (!response.ok) {
    let message =
      `Could not load the finished interior (${response.status}).`;

    try {
      const data =
        await readJsonSafe(
          response.clone()
        );

      message =
        data?.message ||
        data?.error ||
        message;
    } catch {
      // Preserve the controlled fallback message.
    }

    const error =
      new Error(message);

    error.status =
      response.status;

    throw error;
  }

  const blob =
    await response.blob();

  if (
    !blob ||
    Number(blob.size) <= 0
  ) {
    throw new Error(
      "The finished interior PDF is unavailable."
    );
  }

  return blob;
};

api.getChildrensBookPublicationCoverReview = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  const response =
    await fetch(
      buildUrl(
        `/api/projects/childrens-book/${encodeURIComponent(
          id
        )}/publication-production/cover-review`
      ),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept:
            "application/pdf",
        },
      }
    );

  if (!response.ok) {
    let message =
      `Could not load the finished cover (${response.status}).`;

    try {
      const data =
        await readJsonSafe(
          response.clone()
        );

      message =
        data?.message ||
        data?.error ||
        message;
    } catch {
      // Preserve the controlled fallback message.
    }

    const error =
      new Error(message);

    error.status =
      response.status;

    throw error;
  }

  const blob =
    await response.blob();

  if (
    !blob ||
    Number(blob.size) <= 0
  ) {
    throw new Error(
      "The finished cover PDF is unavailable."
    );
  }

  return blob;
};

api.getChildrensBookPublicationFinalInterior = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  const response =
    await fetch(
      buildUrl(
        `/api/projects/childrens-book/${encodeURIComponent(
          id
        )}/publication-production/final-interior`
      ),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept:
            "application/pdf",
        },
      }
    );

  if (!response.ok) {
    let message =
      `Could not load the final publication interior (${response.status}).`;

    try {
      const data =
        await readJsonSafe(
          response.clone()
        );

      message =
        data?.message ||
        data?.error ||
        message;
    } catch {
      // Preserve the controlled fallback message.
    }

    const error =
      new Error(message);

    error.status =
      response.status;

    throw error;
  }

  const blob =
    await response.blob();

  if (
    !blob ||
    Number(blob.size) <= 0
  ) {
    throw new Error(
      "The final publication interior PDF is unavailable."
    );
  }

  return blob;
};

api.getChildrensBookPublicationFinalCover = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  const response =
    await fetch(
      buildUrl(
        `/api/projects/childrens-book/${encodeURIComponent(
          id
        )}/publication-production/final-cover`
      ),
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept:
            "application/pdf",
        },
      }
    );

  if (!response.ok) {
    let message =
      `Could not load the final publication cover (${response.status}).`;

    try {
      const data =
        await readJsonSafe(
          response.clone()
        );

      message =
        data?.message ||
        data?.error ||
        message;
    } catch {
      // Preserve the controlled fallback message.
    }

    const error =
      new Error(message);

    error.status =
      response.status;

    throw error;
  }

  const blob =
    await response.blob();

  if (
    !blob ||
    Number(blob.size) <= 0
  ) {
    throw new Error(
      "The final publication cover PDF is unavailable."
    );
  }

  return blob;
};

api.regenerateChildrensBookPublicationInterior = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  /*
   * This endpoint accepts no publication settings or production
   * authority. Send only an empty JSON object through the existing
   * authenticated POST helper.
   */
  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/publication-production/interior-regeneration`,
    {}
  );
};

api.regenerateChildrensBookPublicationCover = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  /*
   * Final Cover regeneration accepts no publication settings,
   * production authority or asset authority from the browser.
   * Send only an empty JSON object.
   */
  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/publication-production/cover-regeneration`,
    {}
  );
};

api.approveChildrensBookPublicationInterior = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/publication-production/interior-approval`,
    {
      creatorConfirmed:
        true,
    }
  );
};

api.approveChildrensBookPublicationCover = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/publication-production/cover-approval`,
    {
      creatorConfirmed:
        true,
    }
  );
};

api.startChildrensBookPublicationProduction = async (
  projectId,
  {
    paperType = "",
    interiorColor = "",
  } = {}
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/publication-production`,
    {
      paperType:
        String(
          paperType || ""
        )
          .trim()
          .toLowerCase(),

      interiorColor:
        String(
          interiorColor || ""
        )
          .trim()
          .toLowerCase(),
    }
  );
};

api.getChildrensBookAssembly = async (
  projectId,
  {
    offset = 0,
    limit = 20,
  } = {}
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  const requestedOffset =
    Number(offset);

  const requestedLimit =
    Number(limit);

  const safeOffset =
    Number.isInteger(
      requestedOffset
    )
      ? Math.max(
          0,
          requestedOffset
        )
      : 0;

  const safeLimit =
    Number.isInteger(
      requestedLimit
    )
      ? Math.min(
          50,
          Math.max(
            1,
            requestedLimit
          )
        )
      : 20;

  const params =
    new URLSearchParams();

  params.set(
    "offset",
    String(safeOffset)
  );

  params.set(
    "limit",
    String(safeLimit)
  );

  return await api.get(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/assembly?${params.toString()}`
  );
};

api.getChildrensBookAssemblyUnit = async (
  projectId,
  unitId
) => {
  const id =
    String(projectId || "").trim();

  const unit =
    String(unitId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  if (!unit) {
    throw new Error(
      "Book Map unit identifier is required."
    );
  }

  return await api.get(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/assembly/units/${encodeURIComponent(
      unit
    )}`
  );
};

api.approveChildrensBookEdition = async (projectId, expectedRevision) => {
  const id = String(projectId || "").trim();
  if (!id) throw new Error("Children's Book project ID required");
  return api.post(`/api/projects/childrens-book/${encodeURIComponent(id)}/publish-edition`,
    { expectedRevision });
};
api.getChildrensBookPublishedEdition = async (projectId) => {
  const id = String(projectId || "").trim();
  if (!id) throw new Error("Children's Book project ID required");
  return api.get(`/api/projects/childrens-book/${encodeURIComponent(id)}/published-edition`);
};

api.saveChildrensBookBlueprint = async (
  projectId,
  {
    expectedRevision,
    blueprint,
  } = {}
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.put(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/blueprint`,
    {
      expectedRevision,
      blueprint:
        blueprint || {},
    }
  );
};

api.archiveChildrensBookProject = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/archive`,
    {}
  );
};

api.restoreChildrensBookProject = async (
  projectId
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/restore`,
    {}
  );
};

api.previewChildrensBookManuscriptImport = async (
  file
) => {
  if (!file) {
    throw new Error(
      "Choose a DOCX Word manuscript first."
    );
  }

  const form =
    new FormData();

  form.append(
    "file",
    file
  );

  return await request(
    "/api/manuscript-import/preview",
    {
      method: "POST",
      body: form,
    }
  );
};


api.previewManuscriptImport =
  api.previewChildrensBookManuscriptImport;


api.uploadChildrensBookCover = async (
  projectId,
  file
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  if (!file) {
    throw new Error(
      "Choose a Children's Book cover image first."
    );
  }

  const form =
    new FormData();

  form.append(
    "cover",
    file
  );

  return await request(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/uploads/cover`,
    {
      method: "POST",
      body: form,
    }
  );
};


api.uploadChildrensBookIllustration = async (
  projectId,
  bookMapUnitId,
  file
) => {
  const id =
    String(projectId || "").trim();

  const unitId =
    String(
      bookMapUnitId || ""
    ).trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  if (!unitId) {
    throw new Error(
      "Choose a Book Map page or spread for this illustration."
    );
  }

  if (!file) {
    throw new Error(
      "Choose a Children's Book illustration image first."
    );
  }

  const form =
    new FormData();

  form.append(
    "illustration",
    file
  );

  return await request(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/uploads/illustration/${encodeURIComponent(
      unitId
    )}`,
    {
      method: "POST",
      body: form,
    }
  );
};

api.getChildrensBookAssetFile = async (
  fileUrl
) => {
  const rawUrl =
    String(fileUrl || "").trim();

  if (!rawUrl) {
    throw new Error(
      "Children's Book asset file address is required."
    );
  }

  let requestPath =
    rawUrl;

  try {
    const parsed =
      new URL(
        rawUrl,
        "http://nextscenes.local"
      );

    if (
      parsed.pathname.startsWith(
        "/api/"
      )
    ) {
      requestPath =
        `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    requestPath =
      rawUrl;
  }

  const target =
    /^https?:\/\//i.test(
      requestPath
    )
      ? requestPath
      : buildUrl(requestPath);

  const response =
    await fetch(
      target,
      {
        method: "GET",
        credentials: "include",
      }
    );

  if (!response.ok) {
    let message =
      `Could not load the project image (${response.status}).`;

    try {
      const data =
        await readJsonSafe(
          response.clone()
        );

      message =
        data?.message ||
        data?.error ||
        message;
    } catch {
      // Preserve the controlled fallback message.
    }

    const error =
      new Error(message);

    error.status =
      response.status;

    throw error;
  }

  return await response.blob();
};

api.getChildrensBookAssets = async (
  projectId,
  {
    status = "active",
  } = {}
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  const params =
    new URLSearchParams();

  const cleanStatus =
    String(status || "")
      .trim()
      .toLowerCase();

  if (cleanStatus) {
    params.set(
      "status",
      cleanStatus
    );
  }

  const query =
    params.toString();

  return await api.get(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/assets${
      query ? `?${query}` : ""
    }`
  );
};

api.registerChildrensBookAsset = async (
  projectId,
  asset = {}
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/assets`,
    asset || {}
  );
};

api.replaceChildrensBookAsset = async (
  projectId,
  assetId,
  {
    expectedRevision,
    asset,
  } = {}
) => {
  const project =
    String(projectId || "").trim();

  const assetIdentity =
    String(assetId || "").trim();

  if (!project) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  if (!assetIdentity) {
    throw new Error(
      "Children's Book asset identifier is required."
    );
  }

  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      project
    )}/assets/${encodeURIComponent(
      assetIdentity
    )}/replace`,
    {
      ...(asset || {}),
      expectedRevision,
    }
  );
};

api.removeChildrensBookAsset = async (
  projectId,
  assetId,
  expectedRevision
) => {
  const project =
    String(projectId || "").trim();

  const assetIdentity =
    String(assetId || "").trim();

  if (!project) {
    throw new Error(
      "Children's Book project identifier is required."
    );
  }

  if (!assetIdentity) {
    throw new Error(
      "Children's Book asset identifier is required."
    );
  }

  return await api.post(
    `/api/projects/childrens-book/${encodeURIComponent(
      project
    )}/assets/${encodeURIComponent(
      assetIdentity
    )}/remove`,
    {
      expectedRevision,
    }
  );
};

api.getStories = async (opts = {}) => {
  const page = clampInt(opts.page, 1, 1000000, 1);
  const limit = clampInt(opts.limit, 1, 100, 50);

  const query = qsFrom({
    mode: opts.mode || "full",
    page,
    limit,
  });

  return await request(`/api/stories${query}`, { method: "GET" });
};

api.getStoryById = async (id) => {
  const sid = String(id || "").trim();
  if (!sid) throw new Error("Missing story id.");
  return await request(`/api/stories/${encodeURIComponent(sid)}`, {
    method: "GET",
  });
};

// Novel V3D2D: owner-initiated, private, provisional preview only.
// Status reads saved source only on an explicit author click. PDF remains in
// browser memory; no production file is saved, approved or published.
api.getNovelSavedInteriorStatus = async (storyId) => {
  const id = String(storyId || "").trim();
  if (!id) throw new Error("Story identity is required.");
  return await api.get(`/api/stories/${encodeURIComponent(id)}/production-source/status`);
};

// Same owner-only saved-source scope as the private Novel PDF preview.
// GET only: no content returned, no publication or database mutation.
api.getNovelForewordPlacementDiagnostic = async (storyId) => {
  const id = String(storyId || "").trim();
  if (!id) throw new Error("Saved Novel identity is required.");
  return await request(
    `/api/stories/${encodeURIComponent(id)}/production-source/foreword-diagnostic-v1`,
    { method: "GET", cache: "no-store" }
  );
};

api.getNovelPrivateInteriorPreview = async (storyId, expectedFingerprint) => {
  const id = String(storyId || "").trim();
  const fingerprint = String(expectedFingerprint || "").trim();
  if (!id || !/^[a-f0-9]{64}$/i.test(fingerprint)) {
    throw new Error("Verify the current saved manuscript before opening its PDF preview.");
  }
  const path = `/api/stories/${encodeURIComponent(id)}/production-source/private-interior-pdf-v3d2b`;
  async function fetchPdf(forceRefreshCsrf) {
    const csrf = await ensureCsrfToken(forceRefreshCsrf);
    const response = await fetch(buildUrl(path), {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: {
        Accept: "application/pdf",
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ expectedFingerprint: fingerprint }),
    });
    if (!response.ok) {
      const data = await readJsonSafe(response);
      const error = new Error(
        data?.error || data?.message || `Private preview unavailable (${response.status}).`
      );
      error.status = response.status;
      error.data = data;
      throw error;
    }
    if (!/^application\/pdf(?:\s*;|$)/i.test(response.headers.get("content-type") || "")) {
      throw new Error("The server did not return a PDF preview.");
    }
    const pdf = await response.blob();
    if (!pdf.size) throw new Error("The PDF preview was empty.");
    return pdf;
  }
  try {
    return await fetchPdf(false);
  } catch (error) {
    if (looksLikeCsrfFailure(error?.status, error?.data, error?.message)) {
      return await fetchPdf(true);
    }
    throw error;
  }
};

// Private, archive-authoritative Novel PDF proof. This is NOT the editable Story
// preview and cannot lock pages, approve printing or publish a book.
api.getNovelPrivateSnapshotMeasuredInteriorProof = async (
  storyId, snapshotId, expectedFingerprint
) => {
  const sid = String(storyId || "").trim();
  const archiveId = String(snapshotId || "").trim();
  const fingerprint = String(expectedFingerprint || "").trim();
  if (!/^[a-f0-9]{24}$/i.test(sid) || !/^[a-f0-9]{24}$/i.test(archiveId) ||
      !/^[a-f0-9]{64}$/.test(fingerprint)) {
    throw new Error("Verify the saved production snapshot before opening its PDF.");
  }
  const path = `/api/stories/${encodeURIComponent(sid)}/production-source/snapshots/${encodeURIComponent(archiveId)}/private-measured-interior-pdf-v1`;
  async function fetchPdf(forceRefreshCsrf) {
    const csrf = await ensureCsrfToken(forceRefreshCsrf);
    const response = await fetch(buildUrl(path), {
      method: "POST", credentials: "include", cache: "no-store",
      headers: {
        Accept: "application/pdf",
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ expectedFingerprint: fingerprint }),
    });
    if (!response.ok) {
      const data = await readJsonSafe(response);
      const err = new Error(data?.error || data?.message ||
        `Private snapshot interior proof unavailable (${response.status}).`);
      err.status = response.status;
      err.data = data;
      throw err;
    }
    if (!/^application\/pdf(?:\s*;|$)/i.test(response.headers.get("content-type") || "")) {
      throw new Error("The server did not return a private snapshot PDF.");
    }
    const blob = await response.blob();
    if (!blob.size || blob.size > 32 * 1024 * 1024) {
      throw new Error("The private snapshot PDF is empty or exceeds the safe preview size.");
    }
    return blob;
  }
  try {
    return await fetchPdf(false);
  } catch (error) {
    if (looksLikeCsrfFailure(error?.status, error?.data, error?.message)) {
      return await fetchPdf(true);
    }
    throw error;
  }
};

api.getSharedProjectContext = async (id) => {
  const sid = String(id || "").trim();
  if (!sid) throw new Error("Missing story id.");

  return await request(
    `/api/stories/${encodeURIComponent(sid)}/project-context`,
    {
      method: "GET",
    }
  );
};

api.getStoriesBuild = async () => {
  return await request(`/api/stories/__build`, { method: "GET" });
};

api.upgradeToWriter = async (opts = {}) => {
  return await request(`/api/auth/upgrade-to-writer`, {
    method: "POST",
    body: {
      accepted: opts.accepted === true,
      writerAgreementAccepted: opts.accepted === true,
      agreementVersion: opts.agreementVersion || "writer-agreement-v1",
    },
  });
};

api.getNotifications = async (opts = {}) => {
  const query = qsFrom({
    limit: clampInt(opts.limit, 1, 100, 30),
    unreadOnly: opts.unreadOnly === true ? "true" : "",
  });

  return await request(`/api/notifications${query}`, { method: "GET" });
};

api.getNotificationUnreadCount = async () => {
  return await request(`/api/notifications/unread-count`, { method: "GET" });
};

api.markNotificationRead = async (id) => {
  const nid = String(id || "").trim();
  if (!nid) throw new Error("Missing notification id.");
  return await request(`/api/notifications/${encodeURIComponent(nid)}/read`, {
    method: "PATCH",
    body: {},
  });
};

api.markAllNotificationsRead = async () => {
  return await request(`/api/notifications/read-all`, {
    method: "PATCH",
    body: {},
  });
};

api.submitMysteryAttempt = async (mysteryId, picked, opts = {}) => {
  const mid = String(mysteryId || "").trim();
  if (!mid) throw new Error("Missing mystery id.");

  return await request(`/api/mysteries/${encodeURIComponent(mid)}/attempt`, {
    method: "POST",
    body: {
      picked: String(picked || "").trim().toUpperCase(),
      readerKey: String(opts.readerKey || "").trim(),
    },
  });
};

/* -------------------- Public Homepage Manager helpers -------------------- */

api.getPublicHomepage = async (opts = {}) => {
  const query = qsFrom({
    language: opts.language || "en",
  });

  return await request(`/api/public-homepage${query}`, {
    method: "GET",
  });
};

api.getAdminPublicHomepageItems = async (opts = {}) => {
  const query = qsFrom({
    language: opts.language || "",
    section: opts.section || "",
  });

  return await request(`/api/admin/public-homepage/items${query}`, {
    method: "GET",
  });
};

api.getAdminPublicHomepageStories = async (opts = {}) => {
  const query = qsFrom({
    query: opts.query || "",
    language: opts.language || "",
    limit: opts.limit || 100,
  });

  return await request(`/api/admin/public-homepage/stories${query}`, {
    method: "GET",
  });
};

api.getAdminPublicHomepageMysteries = async (opts = {}) => {
  const query = qsFrom({
    query: opts.query || "",
    limit: opts.limit || 100,
  });

  return await request(`/api/admin/public-homepage/mysteries${query}`, {
    method: "GET",
  });
};

api.createAdminPublicHomepageItem = async (payload = {}) => {
  return await request(`/api/admin/public-homepage/items`, {
    method: "POST",
    body: payload || {},
  });
};

api.updateAdminPublicHomepageItem = async (id, payload = {}) => {
  const itemId = String(id || "").trim();
  if (!itemId) throw new Error("Missing homepage item id.");

  return await request(
    `/api/admin/public-homepage/items/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      body: payload || {},
    }
  );
};

api.deleteAdminPublicHomepageItem = async (id) => {
  const itemId = String(id || "").trim();
  if (!itemId) throw new Error("Missing homepage item id.");

  return await request(
    `/api/admin/public-homepage/items/${encodeURIComponent(itemId)}`,
    {
      method: "DELETE",
    }
  );
};

/* -------------------- Story tracking helpers -------------------- */

api.getTrackingBuild = async () => {
  return await request(`/api/tracking/__build`, { method: "GET" });
};

api.getStoryTracking = async (storyId) => {
  const sid = String(storyId || "").trim();
  if (!sid) throw new Error("Missing story id.");
  return await request(`/api/tracking/story/${encodeURIComponent(sid)}`, {
    method: "GET",
  });
};

api.recordStoryTrackingView = async (storyId, opts = {}) => {
  const sid = String(storyId || "").trim();
  if (!sid) throw new Error("Missing story id.");

  return await request(`/api/tracking/story/${encodeURIComponent(sid)}/view`, {
    method: "POST",
    body: {
      surface: opts.surface || "general",
    },
  });
};

api.markStoryTrackingSeen = async (storyId, opts = {}) => {
  const sid = String(storyId || "").trim();
  if (!sid) throw new Error("Missing story id.");

  return await request(`/api/tracking/story/${encodeURIComponent(sid)}/seen`, {
    method: "POST",
    body: {
      surface: opts.surface || "general",
      markAll: opts.markAll === true,
      markCanon: opts.markCanon === true,
      markProposals: opts.markProposals === true,
      markComments: opts.markComments === true,
    },
  });
};

api.recordStorySceneProgress = async (storyId, sceneId, opts = {}) => {
  const sid = String(storyId || "").trim();
  const scid = String(sceneId || "").trim();
  if (!sid) throw new Error("Missing story id.");
  if (!scid) throw new Error("Missing scene id.");

  return await request(`/api/tracking/story/${encodeURIComponent(sid)}/scene-progress`, {
    method: "POST",
    body: {
      surface: opts.surface || "readerView",
      sceneId: scid,
      sceneNumber: opts.sceneNumber || null,
      sceneTitle: opts.sceneTitle || "",
      action: opts.action || "opened",
    },
  });
};

api.updateChildrensBookVisibility = async (
  projectId,
  visibility
) => {
  const id =
    String(projectId || "").trim();

  if (!id) {
    throw new Error(
      "Project identifier is required."
    );
  }

  const nextVisibility =
    String(visibility || "")
      .trim()
      .toLowerCase();

  if (
    ![
      "private_draft",
      "nextscenes_readers",
      "published",
    ].includes(nextVisibility)
  ) {
    throw new Error(
      "Choose Private, NextScenes Readers, or Public."
    );
  }

  return await api.put(
    `/api/projects/childrens-book/${encodeURIComponent(
      id
    )}/visibility`,
    {
      visibility:
        nextVisibility,
    }
  );
};


// Novel Final Print Approval UI V1. These are inert unless explicitly invoked
// and additionally guarded by backend owner, pilot and feature checks.
function novelPaperbackInteriorPath(storyId, snapshotId) {
  const s = String(storyId || "").trim();
  const a = String(snapshotId || "").trim();
  if (!/^[a-f0-9]{24}$/i.test(s) || !/^[a-f0-9]{24}$/i.test(a)) {
    throw new Error("A verified saved Novel and archive identity are required.");
  }
  return `/api/stories/${encodeURIComponent(s)}/production-source/snapshots/${encodeURIComponent(a)}/paperback-interior-v1`;
}
api.getNovelProductionSnapshotForFinalPrint = async (storyId, snapshotId) => {
  const base = novelPaperbackInteriorPath(storyId, snapshotId);
  return await api.get(base.replace(/\/paperback-interior-v1$/, ""));
};
api.getNovelPaperbackInteriorCurrent = async (storyId, snapshotId) =>
  await api.get(novelPaperbackInteriorPath(storyId, snapshotId) + "/current");
api.prepareNovelPaperbackInterior = async (storyId, snapshotId, expectedFingerprint) => {
  if (!/^[a-f0-9]{64}$/.test(String(expectedFingerprint || ""))) {
    throw new Error("The exact archive fingerprint is required.");
  }
  return await api.post(novelPaperbackInteriorPath(storyId, snapshotId) + "/prepare",
    { expectedFingerprint });
};
api.approveNovelPaperbackInterior = async (storyId, snapshotId, artifactId,
  expectedPdfSha256, acknowledgment) => {
  if (!/^[a-f0-9]{24}$/i.test(String(artifactId || "")) ||
      !/^[a-f0-9]{64}$/.test(String(expectedPdfSha256 || "")) ||
      acknowledgment !== "APPROVE AND LOCK THIS EXACT PDF") {
    throw new Error("Exact creator approval identity and acknowledgment required.");
  }
  return await api.post(novelPaperbackInteriorPath(storyId, snapshotId) + "/" +
    encodeURIComponent(artifactId) + "/approve-and-lock", { expectedPdfSha256, acknowledgment });
};
api.getNovelPaperbackInteriorPdf = async (storyId, snapshotId, artifactId, role) => {
  if (!/^[a-f0-9]{24}$/i.test(String(artifactId || "")) ||
      !["review", "final"].includes(role)) throw new Error("Exact PDF identity and role required.");
  const path = novelPaperbackInteriorPath(storyId, snapshotId) + "/" +
    encodeURIComponent(artifactId) + "/" + role + ".pdf";
  const res = await fetch(buildUrl(path), {
    method: "GET", credentials: "include", cache: "no-store",
    headers: { Accept: "application/pdf" },
  });
  if (!res.ok) {
    const data = await readJsonSafe(res);
    const err = new Error(data?.error || data?.code || `Exact PDF unavailable (${res.status}).`);
    err.status = res.status; err.data = data; throw err;
  }
  if (!/^application\/pdf(?:\s*;|$)/i.test(res.headers.get("content-type") || "")) {
    throw new Error("The server did not return an exact PDF.");
  }
  const blob = await res.blob();
  if (blob.size < 100 || blob.size > 8 * 1024 * 1024) {
    throw new Error("Exact PDF byte count is outside the safe V1 limit.");
  }
  return { blob, sha256: res.headers.get("X-NextScenes-Exact-PDF-SHA256") || "",
    lockedPageCount: res.headers.get("X-NextScenes-Locked-Page-Count") };
};

// Owner-only, read-only metadata index. This does not inspect the CURRENT
// manuscript fingerprint, capture any snapshot, or mutate publication state.
api.getNovelApprovedVersionsV1 = async (storyId, page = 0) => {
  const id = String(storyId || "").trim();
  if (!/^[a-f0-9]{24}$/i.test(id) || !Number.isInteger(page) ||
      page < 0 || page > 9999) {
    throw new Error("Exact Novel identity and history page required.");
  }
  return api.get("/api/stories/" + encodeURIComponent(id) +
    "/production-source/approved-versions-v1?page=" + page);
};

// B2C exact paperback COVER + INTERIOR pair. Additive opt-in helpers only.
// Authorization, pilot scope, lock state, transactions and disabled-by-default
// feature flags are verified again by the server for every request.
function novelPaperbackCoverPath(storyId, snapshotId, interiorArtifactId) {
  const id = String(interiorArtifactId || "").trim();
  if (!/^[a-f0-9]{24}$/i.test(id)) throw new Error("Exact locked interior artifact identity required.");
  return novelPaperbackInteriorPath(storyId, snapshotId)
    .replace(/\/paperback-interior-v1$/, "/paperback-cover-b2c-v1/" + encodeURIComponent(id));
}
api.getNovelPaperbackCoverStatus = async (storyId, snapshotId, interiorArtifactId) =>
  api.get(novelPaperbackCoverPath(storyId, snapshotId, interiorArtifactId) + "/current");
api.prepareNovelPaperbackCover = async (storyId, snapshotId, interiorArtifactId, data) => {
  if (!/^[a-f0-9]{64}$/.test(data?.expectedFingerprint || "") ||
      !/^[a-f0-9]{64}$/.test(data?.expectedInteriorPdfSha256 || "") ||
      !Number.isInteger(data?.expectedLatestRevision) || data.expectedLatestRevision < 0 ||
      data.expectedLatestRevision >= 10000 ||
      !/^#[a-fA-F0-9]{6}$/.test(data?.spineBackgroundHex || "")) {
    throw new Error("Verified cover revision, archive and locked interior are required.");
  }
  return api.post(novelPaperbackCoverPath(storyId, snapshotId, interiorArtifactId) + "/prepare", data);
};
api.approveNovelPaperbackCover = async (storyId, snapshotId, interiorArtifactId,
  coverArtifactId, data) => {
  if (!/^[a-f0-9]{24}$/i.test(String(coverArtifactId || "")) ||
      !Number.isInteger(data?.expectedRevision) || data.expectedRevision < 1 ||
      !/^[a-f0-9]{64}$/.test(data?.expectedCoverPdfSha256 || "") ||
      !/^[a-f0-9]{64}$/.test(data?.expectedInteriorPdfSha256 || "") ||
      data?.acknowledgment !== "APPROVE AND LOCK THIS EXACT COVER PDF") {
    throw new Error("Exact cover and interior hashes and creator acknowledgment required.");
  }
  return api.post(novelPaperbackCoverPath(storyId, snapshotId, interiorArtifactId) +
    "/" + encodeURIComponent(coverArtifactId) + "/approve-and-lock", data);
};
api.getNovelPaperbackCoverPdf = async (storyId, snapshotId, interiorArtifactId,
  coverArtifactId, kind) => {
  const base = novelPaperbackCoverPath(storyId, snapshotId, interiorArtifactId);
  let suffix;
  if (kind === "review" && /^[a-f0-9]{24}$/i.test(String(coverArtifactId || ""))) {
    suffix = "/" + encodeURIComponent(coverArtifactId) + "/review.pdf";
  } else if (kind === "final-cover") suffix = "/final/cover.pdf";
  else if (kind === "final-interior") suffix = "/final/interior.pdf";
  else throw new Error("An exact cover review or approved PDF type is required.");
  const response = await fetch(buildUrl(base + suffix), {
    method: "GET", credentials: "include", cache: "no-store",
    headers: { Accept: "application/pdf" },
  });
  if (!response.ok) {
    const data = await readJsonSafe(response);
    const err = new Error(data?.error || data?.code || `Approved PDF unavailable (${response.status}).`);
    err.status = response.status; err.data = data; throw err;
  }
  if (!/^application\/pdf(?:\s*;|$)/i.test(response.headers.get("content-type") || "")) {
    throw new Error("The server did not return an exact PDF.");
  }
  const blob = await response.blob();
  if (blob.size < 500 || blob.size > 8 * 1024 * 1024) {
    throw new Error("Paperback PDF bytes are outside the safe V1 size limit.");
  }
  return { blob, sha256: response.headers.get("X-NextScenes-Exact-PDF-SHA256") || "",
    matchingSha256: response.headers.get("X-NextScenes-Matching-PDF-SHA256") || "",
    printStatus: response.headers.get("X-NextScenes-Print-Status") || "" };
};
